import React, { useState } from 'react';
import axios from 'axios';

export default function RatingModal({
    orderItem,
    order,
    isOpen = true,
    onClose,
    onSubmit,
    token,
    apiBaseUrl,
    existingReview
}) {
    const [rating, setRating] = useState(existingReview?.rating || 0);
    const [hoverRating, setHoverRating] = useState(0);
    const [reviewDescription, setReviewDescription] = useState(existingReview?.description || '');
    const [reviewImages, setReviewImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState(existingReview?.images || []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    if (!isOpen || !orderItem) return null;

    const handleStarClick = (starValue) => {
        setRating(starValue);
    };

    const handleStarHover = (starValue) => {
        setHoverRating(starValue);
    };

    const handleStarHoverLeave = () => {
        setHoverRating(0);
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);

        if (files.length + imagePreviews.length > 5) {
            setSubmitError('You can upload a maximum of 5 images');
            return;
        }

        const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            setSubmitError('Some files exceed 5MB limit. Please choose smaller images.');
            return;
        }

        setReviewImages([...reviewImages, ...files]);
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setImagePreviews([...imagePreviews, ...newPreviews]);
    };

    const handleRemoveImage = (index) => {
        const newImages = reviewImages.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);

        if (typeof imagePreviews[index] === 'string' && imagePreviews[index].startsWith('blob:')) {
            URL.revokeObjectURL(imagePreviews[index]);
        }

        setReviewImages(newImages);
        setImagePreviews(newPreviews);
    };

    const handleSubmitReview = async () => {
        setSubmitError('');

        if (rating === 0) {
            setSubmitError('Please select a rating');
            return;
        }

        if (!reviewDescription.trim()) {
            setSubmitError('Please write a review description');
            return;
        }

        if (reviewDescription.trim().length < 10) {
            setSubmitError('Review description must be at least 10 characters long');
            return;
        }

        setIsSubmitting(true);

        try {
            console.log('Submitting material:', orderItem.material);

            const formData = new FormData();
            formData.append('orderitem', orderItem.id);
            formData.append('rating', rating);
            formData.append('description', reviewDescription.trim());
            formData.append('material', orderItem.material.id);  // ✅ ADD THIS - auto-populate from orderItem


            // Append multiple images
            reviewImages.forEach((image) => {
                formData.append('images', image);
            });

            let response;

            if (existingReview) {
                response = await axios.put(
                    `${apiBaseUrl}/reviews/${existingReview.id}/`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            } else {
                response = await axios.post(
                    `${apiBaseUrl}/reviews/`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            }

            // Pass review data to parent
            onSubmit({
                id: response.data.id,
                rating: response.data.rating,
                description: response.data.description,
                images: response.data.images?.map(img => img.image_url || img.image) || imagePreviews,
                date: response.data.created_at || new Date(),
                material: response.data.materials,  // ✅ NEW
                material_name: response.data.material_name,
            });

            alert('✅ Review submitted successfully!');
        } catch (error) {
            console.error('Error submitting review:', error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.orderitem?.[0] ||
                error.response?.data?.material?.[0] ||  // ✅ NEW

                error.response?.data?.rating?.[0] ||
                error.response?.data?.description?.[0] ||
                'Failed to submit review. Please try again.';
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay rating-modal-overlay" onClick={() => !isSubmitting && onClose()}>
            <div className="modal-content rating-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h3>{existingReview ? 'Edit Your Review' : 'Rate This Item'}</h3>
                        <p className="order-number">Order #{order.order_number} - {orderItem.product_type_name}</p>
                    </div>
                    <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
                        <i className="ph ph-x"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <div className="rating-form">
                        {/* Item Info */}
                        <div className="item-info">
                            <p><strong>Product:</strong> {orderItem.product_type_name}</p>
                            <p><strong>Material:</strong> {orderItem.material_name}</p>
                            <p><strong>Quantity:</strong> {orderItem.quantity}</p>
                        </div>

                        {/* Star Rating */}
                        <div className="rating-stars-container">
                            <label>How would you rate this item? *</label>
                            <div className="rating-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                        key={star}
                                        className="star"
                                        onClick={() => handleStarClick(star)}
                                        onMouseEnter={() => handleStarHover(star)}
                                        onMouseLeave={handleStarHoverLeave}
                                    >
                                        {star <= (hoverRating || rating) ? (
                                            <i className="ph-fill ph-star" style={{ color: '#fbbf24', fontSize: '40px' }}></i>
                                        ) : (
                                            <i className="ph ph-star" style={{ color: '#d1d5db', fontSize: '40px' }}></i>
                                        )}
                                    </span>
                                ))}
                            </div>
                            {rating > 0 && (
                                <span className="rating-text">{rating} out of 5 stars</span>
                            )}
                        </div>

                        {/* Review Description */}
                        <div className="review-description-container">
                            <label htmlFor="review-text" className="review-label">Write Your Review *</label>
                            <textarea
                                id="review-text"
                                className="review-textarea"
                                placeholder="Share your experience with this item..."
                                value={reviewDescription}
                                onChange={(e) => setReviewDescription(e.target.value)}
                                rows="5"
                                maxLength="500"
                                disabled={isSubmitting}
                            />
                            <span className="character-count">
                                {reviewDescription.length}/500 characters
                            </span>
                        </div>

                        {/* Image Upload */}
                        <div className="review-images-container">
                            <label className="review-label">Upload Images (Optional)</label>
                            <p className="upload-hint">You can upload up to 5 images (max 5MB each)</p>

                            <div className="image-upload-grid">
                                {imagePreviews.map((preview, index) => (
                                    <div key={index} className="image-preview-item">
                                        <img src={preview} alt={`Review ${index + 1}`} />
                                        <button
                                            className="remove-image-btn"
                                            onClick={() => handleRemoveImage(index)}
                                            type="button"
                                            disabled={isSubmitting}
                                        >
                                            <i className="ph ph-x"></i>
                                        </button>
                                    </div>
                                ))}

                                {imagePreviews.length < 5 && (
                                    <label className="upload-image-btn">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleImageUpload}
                                            style={{ display: 'none' }}
                                            disabled={isSubmitting}
                                        />
                                        <div>
                                            <i className="ph ph-image" style={{ fontSize: '40px', color: '#fbbf24' }}></i>
                                            <span>Add Image</span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Error Message */}
                        {submitError && (
                            <p className="error-text">
                                <i className="ph ph-warning-circle"></i> {submitError}
                            </p>
                        )}
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        className="btn btn-outline-main"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-main submit-review-btn"
                        onClick={handleSubmitReview}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="ph ph-circle-notch" style={{ animation: 'spin 1s linear infinite' }}></i>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="ph ph-paper-plane-tilt"></i>
                                {existingReview ? 'Update Review' : 'Submit Review'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
